import { Section } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import {
  COMMERCIAL_LEASE_AG_FACILITIES_APPLICANT_INFO,
  COMMERCIAL_LEASE_AG_FACILITIES_APPLICANT_ADDRESS,
  COMMERCIAL_LEASE_AG_FACILITIES_CO_APPLICANT_INFO,
  COMMERCIAL_LEASE_AG_FACILITIES_BUSINESS_INFO,
  COMMERCIAL_LEASE_AG_FACILITIES_FIXTURE_INFO,
  COMMERCIAL_LEASE_AG_FACILITIES_LEGAL_TITLE_HOLDER_INFO,
  COMMERCIAL_LEASE_AG_FACILITIES_LIEN_HOLDER_INFO,
  COMMERCIAL_LEASE_AG_FACILITIES_FIXTURE_USE_INFO,
  COMMERCIAL_LEASE_AG_FACILITIES_INSURANCE_INFO,
  COMMERCIAL_LEASE_AG_FACILITIES_INSURANCE_AGENT_INFO,
  COMMERCIAL_LEASE_AG_FACILITIES_PROJECT_INFO,
  COMMERCIAL_LEASE_AG_FACILITIES_PROJECT_SITE_INFO,
  COMMERCIAL_LEASE_AG_FACILITIES_ADDITIONAL_INFO,
  COMMERCIAL_LEASE_AG_FACILITIES_SSN,
  COMMERCIAL_LEASE_AG_FACILITIES_FINAL,
} from 'app/models/sections/constants';
import { getCommercialLeaseAgFacilitiesQuestionsByName } from 'app/models/questions/commercial-lease-ag-facilities';
import { ConfirmationCommercial } from 'app/components/ConfirmationCommercial';
import { handleCommercialFormSubmit } from 'app/actions/form/actions';
import { CollectAgFacilitiesSsnStep } from 'app/components/CollectAgFacilitiesSsnStep';

export const getCommercialLeaseAgFacilitiesSectionById = (id: number): Section => {
  return COMMERCIAL_LEASE_AG_FACILITIES_SECTIONS.find((section) => section.id === id) as Section;
};

const COMMERCIAL_LEASE_AG_FACILITIES_SECTIONS: Section[] = [
  {
    id       : COMMERCIAL_LEASE_AG_FACILITIES_APPLICANT_INFO,
    questions: getCommercialLeaseAgFacilitiesQuestionsByName([
      FieldNames.firstName,
      FieldNames.middleName,
      FieldNames.lastName,
      FieldNames.suffixName,
      FieldNames.email,
      FieldNames.phone,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_AG_FACILITIES_APPLICANT_ADDRESS,
    questions: getCommercialLeaseAgFacilitiesQuestionsByName([
      FieldNames.applicantStreet,
      FieldNames.applicantStreet2,
      FieldNames.applicantCity,
      FieldNames.applicantState,
      FieldNames.applicantZip,
      FieldNames.applicantCounty,
      FieldNames.occupancyStartDate,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_AG_FACILITIES_CO_APPLICANT_INFO,
    questions: getCommercialLeaseAgFacilitiesQuestionsByName([
      FieldNames.coBorrowerYN,
      FieldNames.coBorrowerFirstName,
      FieldNames.coBorrowerMiddleName,
      FieldNames.coBorrowerLastName,
      FieldNames.coBorrowerSuffixName,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_AG_FACILITIES_BUSINESS_INFO,
    questions: getCommercialLeaseAgFacilitiesQuestionsByName([
      FieldNames.majorAgProduct,
      FieldNames.grossFarmIncome,
      FieldNames.nonFarmIncome,
      FieldNames.totalAssets,
      FieldNames.totalLiabilities,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_AG_FACILITIES_FIXTURE_INFO,
    questions: getCommercialLeaseAgFacilitiesQuestionsByName([
      FieldNames.realEstateCounty,
      FieldNames.realEstateTownship,
      FieldNames.realEstateSection,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_AG_FACILITIES_FIXTURE_USE_INFO,
    questions: getCommercialLeaseAgFacilitiesQuestionsByName([
      FieldNames.facilityUseAndDimensions,
      FieldNames.projectedProjectCost,
    ]),
    title : 'Last few questions about the real estate...',
  },
  {
    id       : COMMERCIAL_LEASE_AG_FACILITIES_LEGAL_TITLE_HOLDER_INFO,
    questions: getCommercialLeaseAgFacilitiesQuestionsByName([
      FieldNames.legalTitleHolderList,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_AG_FACILITIES_LIEN_HOLDER_INFO,
    questions: getCommercialLeaseAgFacilitiesQuestionsByName([
      FieldNames.lienHolderList,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_AG_FACILITIES_INSURANCE_INFO,
    title    : 'Tell us about your insurance',
    questions: getCommercialLeaseAgFacilitiesQuestionsByName([
      FieldNames.insuranceCompanyName,
      FieldNames.insuranceCompanyStreet,
      FieldNames.insuranceCompanyStreet2,
      FieldNames.insuranceCompanyCity,
      FieldNames.insuranceCompanyState,
      FieldNames.insuranceCompanyZip,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_AG_FACILITIES_INSURANCE_AGENT_INFO,
    questions: getCommercialLeaseAgFacilitiesQuestionsByName([
      FieldNames.insuranceAgentFirstName,
      FieldNames.insuranceAgentMiddleName,
      FieldNames.insuranceAgentLastName,
      FieldNames.insuranceAgentSuffixName,
      FieldNames.insuranceAgentEmail,
      FieldNames.insuranceAgentPhone,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_AG_FACILITIES_PROJECT_INFO,
    title    : 'Tell us about the project site',
    questions: getCommercialLeaseAgFacilitiesQuestionsByName([
      FieldNames.facilityAlongPublicRoad,
      FieldNames.facilityHasWellAndElectric,
      FieldNames.facilityFreeOfExistingBuildings,
      FieldNames.facilityFreeOfMortgages,
      FieldNames.pollutionControlPermitsObtained,
      FieldNames.zoningPermitsObtained,
      FieldNames.surveyCompleted,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_AG_FACILITIES_PROJECT_SITE_INFO,
    questions: getCommercialLeaseAgFacilitiesQuestionsByName([
      FieldNames.facilityAcreage,
      FieldNames.recordOwnerFirstName,
      FieldNames.recordOwnerMiddleName,
      FieldNames.recordOwnerLastName,
      FieldNames.recordOwnerSuffixName,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_AG_FACILITIES_ADDITIONAL_INFO,
    title    : 'Additional information',
    questions: getCommercialLeaseAgFacilitiesQuestionsByName([
      FieldNames.hasTaxLiens,
      FieldNames.hasBankruptcy,
      FieldNames.isPartyToLawsuit,
      FieldNames.hasTaxDelinquency,
      FieldNames.isCosignerOrGuarantor,
      FieldNames.paysAlimony,
      FieldNames.othersOwnBalanceSheetInterest,
      FieldNames.hasUndergroundStorageTanks,
      FieldNames.hasRecentSignificantConstruction,
      FieldNames.isOnGovtHazardWasteList,
      FieldNames.hasImproperlyDisposedHazardous,
      FieldNames.awareOfFarmHazardousWaste,
    ]),
  },
  {
    id          : COMMERCIAL_LEASE_AG_FACILITIES_SSN,
    ifNoQuestions: CollectAgFacilitiesSsnStep,
    submitAction: handleCommercialFormSubmit,
  },
  {
    id              : COMMERCIAL_LEASE_AG_FACILITIES_FINAL,
    ifNoQuestions   : ConfirmationCommercial,
    hideProgressBar : true,
  },
];
